'use client';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { prependSlash } from '@repo/ui/src/components/Anchor/helpers';
import { ToutContentCard } from '@repo/ui/src/components/ToutContent/_toutContentCard';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { classNames } from '@repo/ui/src/utils/classNames';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import React from 'react';
import NoResultsFound from './_NoResultsFound';
import type { ImageProps } from '@repo/ui/src/types/image';
import type { ToutContentProps } from '@repo/ui/src/types/toutContent';
import type { AlignH, AlignV } from '@repo/ui/src/types';

const COL_WIDTHS = [
  '',
  'md:col-span-2 lg:col-span-2 xl:col-span-2',
  'md:col-span-3 lg:col-span-3 xl:col-span-3',
  'md:col-span-4 lg:col-span-4 xl:col-span-4',
];

interface NoResultsContent {
  title?: string;
  text?: string;
  image?: ImageProps;
}

interface ContentProps {
  noResultsContent?: NoResultsContent;
  [key: string]: any;
}

export interface GridLifestyleCard {
  cardIndex: number;
  cardTileWidth?: number;
  background: ImageProps;
  toutContent: ToutContentProps;
  horizontalAlignment: AlignH;
  verticalAlignment: AlignV;
  [key: string]: any;
}

interface PaginatedGridInnerProps {
  items: Array<any>;
  totalPages: number;
  isLoading?: boolean;
  loadingSlot?: React.ReactNode;
  page: number;
  gridLifestyleCard?: GridLifestyleCard;
  viewMore: () => Promise<void>;
  content?: ContentProps;
  GridItemComponent: React.ComponentType<any>;
  hideAddToCartButton?: boolean;
  hideDiscount?: boolean;
  showFinancing?: boolean;
  financingText?: boolean;
  itemClassName?: string;
  isItemHidden?: (item: any) => boolean;
}

const PaginatedGridInner = ({
  items,
  totalPages,
  isLoading,
  loadingSlot,
  page,
  gridLifestyleCard,
  viewMore,
  content,
  GridItemComponent,
  hideAddToCartButton,
  hideDiscount,
  showFinancing,
  financingText,
  itemClassName,
  isItemHidden,
}: PaginatedGridInnerProps) => {
  const { path, query } = useCurrentUrl();
  const { t } = useTranslation();
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const listRef = React.useRef<HTMLUListElement>(null);
  const firstNewItemIndexRef = React.useRef<number | null>(null);

  const disableBtn = isLoading || isLoadingMore;

  const onViewMore = async (e: React.MouseEvent<HTMLAnchorElement>): Promise<void> => {
    e.preventDefault();
    if (!disableBtn) {
      firstNewItemIndexRef.current = items.length;
      setIsLoadingMore(true);
      try {
        await viewMore();
      } catch (error) {
        console.error('Error loading more items:', error instanceof Error ? error.message : error);
      } finally {
        setIsLoadingMore(false);
      }
    }
  };

  React.useEffect(() => {
    if (isLoadingMore || firstNewItemIndexRef.current === null || !listRef.current) return;
    const targetLi = listRef.current.querySelector<HTMLLIElement>(
      `[data-item-index="${firstNewItemIndexRef.current}"]`,
    );
    const firstLink = targetLi?.querySelector<HTMLAnchorElement>('a');
    firstLink?.focus();
    firstNewItemIndexRef.current = null;
  }, [isLoadingMore]);

  const linkPath = React.useMemo(() => {
    const params = new URLSearchParams(query);
    params.set('paginated', 'true');
    params.set('page', (page + 1).toString());
    const basePath = prependSlash(path).replace(/\?.*/, '');
    return `${basePath}?${params.toString()}`;
  }, [path, query, page]);

  return (
    <div className="w-full">
      <div className="relative">
        {!items.length && <NoResultsFound content={content?.noResultsContent} />}
        <ul ref={listRef} className="grid grid-cols-2 p-0 list-none sm:grid-cols-3 lg:grid-cols-4">
          {items?.map((item, i) => (
            <React.Fragment
              key={
                item?.objectID ??
                item?.sku ??
                item?.handle ??
                item?._meta?.deliveryKey ??
                item?.id ??
                i
              }
            >
              {gridLifestyleCard && i === gridLifestyleCard.cardIndex && (
                <li
                  className={classNames(
                    itemClassName,
                    COL_WIDTHS[(gridLifestyleCard.cardTileWidth ?? 1) - 1],
                  )}
                >
                  <ToutContentCard
                    {...gridLifestyleCard}
                    className="flex items-center justify-start h-full"
                    cardBackground={gridLifestyleCard.background}
                    fullWidthAndHeight
                  />
                </li>
              )}
              <li
                data-item-index={i}
                className={classNames(itemClassName, isItemHidden?.(item) && 'hidden')}
              >
                <GridItemComponent
                  item={item}
                  content={content}
                  hideAddToCartButton={hideAddToCartButton}
                  hideDiscount={hideDiscount}
                  showFinancing={showFinancing}
                  financingText={financingText}
                />
              </li>
            </React.Fragment>
          ))}
        </ul>
        {isLoading && loadingSlot && (
          <div className="absolute inset-0 grid bg-transparent-light-500 place-content-center">
            {loadingSlot}
          </div>
        )}
      </div>
      {(page < totalPages || isLoadingMore) && (
        <div className="flex justify-center p-8" suppressHydrationWarning>
          <Button
            as={Anchor}
            variant={ButtonVariant.tertiary}
            link={linkPath}
            data-type="append"
            data-page={page}
            onClick={onViewMore}
          >
            {t('gridContent.viewMoreButtonText', undefined, { default: 'View More' })}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PaginatedGridInner;
